import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, CreditCard, Loader, CheckCircle, Truck, Mail, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const CheckoutForm = ({ cart, cartTotal, clearCart }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  // State management
  const [step, setStep] = useState(1); // 1: contact, 2: shipping address, 3: shipping method, 4: payment
  const [loading, setLoading] = useState(false);
  const [loadingShipping, setLoadingShipping] = useState(false);
  
  // Contact info
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Shipping address
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    phone: ''
  });
  const [addressErrors, setAddressErrors] = useState({});
  
  // Shipping method
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  
  // Payment
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState('');
  const [paymentIntent, setPaymentIntent] = useState(null);

  // Validation
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateAddress = () => {
    const errors = {};
    if (!shippingAddress.name.trim()) errors.name = 'Name is required';
    if (!shippingAddress.address1.trim()) errors.address1 = 'Address is required';
    if (!shippingAddress.city.trim()) errors.city = 'City is required';
    if (!shippingAddress.state.trim()) errors.state = 'State is required';
    if (!shippingAddress.zip.trim()) errors.zip = 'ZIP code is required';
    if (!shippingAddress.country.trim()) errors.country = 'Country is required';
    
    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Fetch shipping rates when address is complete
  const fetchShippingRates = async () => {
    if (!validateAddress()) {
      return;
    }

    try {
      setLoadingShipping(true);
      
      // Calculate total weight and dimensions (estimates for sneakers)
      const totalWeight = cart.reduce((sum, item) => sum + (item.quantity * 2), 0); // 2 lbs per pair
      
      const response = await axios.post(`${API}/shipping/rates`, {
        address_to: {
          name: shippingAddress.name,
          street1: shippingAddress.address1,
          street2: shippingAddress.address2,
          city: shippingAddress.city,
          state: shippingAddress.state,
          zip: shippingAddress.zip,
          country: shippingAddress.country
        },
        weight: totalWeight,
        length: 14,
        width: 10,
        height: 6
      });
      
      setShippingRates(response.data);
      
      // Auto-select cheapest option
      if (response.data.length > 0) {
        const cheapest = response.data.reduce((min, rate) => 
          rate.amount < min.amount ? rate : min
        );
        setSelectedShipping(cheapest);
      }
      
    } catch (error) {
      console.error('Error fetching shipping rates:', error);
      toast.error('Failed to load shipping options. Please check your address.');
    } finally {
      setLoadingShipping(false);
    }
  };

  // Handle next button for each step
  const handleNext = async () => {
    if (step === 1) {
      // Validate email
      if (!validateEmail(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }
      setEmailError('');
      setStep(2);
    } else if (step === 2) {
      // Validate address and fetch shipping
      if (validateAddress()) {
        await fetchShippingRates();
        setStep(3);
      }
    } else if (step === 3) {
      // Validate shipping selection
      if (!selectedShipping) {
        toast.error('Please select a shipping method');
        return;
      }
      
      // Create payment intent
      await createPaymentIntent();
      setStep(4);
    }
  };

  const createPaymentIntent = async () => {
    try {
      setLoading(true);
      
      const shippingCost = selectedShipping?.amount || 0;
      const totalWithShipping = cartTotal + shippingCost;

      const requestBody = {
        amount: parseFloat(totalWithShipping.toFixed(2)),
        currency: 'usd',
        metadata: {
          customer_email: email,
          customer_name: shippingAddress.name,
          customer_phone: shippingAddress.phone,
          shipping_carrier: selectedShipping?.carrier || '',
          shipping_service: selectedShipping?.service_level || '',
          shipping_cost: shippingCost.toFixed(2),
          shipping_address: `${shippingAddress.address1}, ${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.zip}`,
          source: 'web_checkout_inline',
          cart_items: JSON.stringify(cart.map(item => ({
            product_id: item.productId || item.id,
            variant_id: item.variantId || '',
            product_name: item.name || item.product?.name,
            size: item.size || item.variant?.size,
            color: item.color || item.variant?.color,
            quantity: item.quantity || 1,
            unit_price: item.price || item.product?.price || 0
          })))
        }
      };

      const response = await axios.post(`${API}/payments/create-intent`, requestBody);
      setPaymentIntent(response.data);
      
    } catch (error) {
      console.error('Error creating payment intent:', error);
      toast.error('Failed to initialize payment. Please try again.');
      setStep(3); // Go back to shipping selection
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || !paymentIntent) {
      return;
    }

    if (!cardComplete) {
      setCardError('Please enter valid card details');
      return;
    }

    try {
      setLoading(true);

      const { error, paymentIntent: confirmedIntent } = await stripe.confirmCardPayment(
        paymentIntent.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: shippingAddress.name,
              email: email,
              phone: shippingAddress.phone,
              address: {
                line1: shippingAddress.address1,
                line2: shippingAddress.address2,
                city: shippingAddress.city,
                state: shippingAddress.state,
                postal_code: shippingAddress.zip,
                country: shippingAddress.country
              }
            },
          },
        }
      );

      if (error) {
        console.error('Payment error:', error);
        setCardError(error.message);
        toast.error(error.message);
      } else if (confirmedIntent.status === 'succeeded') {
        // Confirm on backend
        await axios.post(`${API}/payments/confirm-intent?payment_intent_id=${confirmedIntent.id}`);
        
        // Clear cart and show success
        clearCart();
        toast.success('Payment successful! Thank you for your purchase.');
        
        // Navigate to success page
        navigate('/checkout/success');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const subtotal = cartTotal;
  const shippingCost = selectedShipping?.amount || 0;
  const tax = 0; // Can be calculated based on address if needed
  const total = subtotal + shippingCost + tax;

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center py-12">
            <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">Add some sneakers to get started!</p>
            <button
              onClick={() => navigate('/collection')}
              className="bg-brand-purple text-white px-6 py-3 rounded-lg hover:bg-purple-700"
            >
              Shop Collection
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left side - Checkout Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              
              {/* Progress Indicator */}
              <div className="mb-8">
                <div className="flex items-center justify-between">
                  {[1, 2, 3, 4].map((num) => (
                    <React.Fragment key={num}>
                      <div className={`flex items-center ${num < 4 ? 'flex-1' : ''}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          step >= num ? 'bg-brand-purple text-white' : 'bg-gray-200 text-gray-500'
                        }`}>
                          {step > num ? <CheckCircle className="w-5 h-5" /> : num}
                        </div>
                        <div className="ml-2 text-sm font-medium text-gray-700">
                          {num === 1 && 'Contact'}
                          {num === 2 && 'Shipping'}
                          {num === 3 && 'Method'}
                          {num === 4 && 'Payment'}
                        </div>
                      </div>
                      {num < 4 && <div className={`flex-1 h-1 mx-2 ${step > num ? 'bg-brand-purple' : 'bg-gray-200'}`} />}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Step 1: Contact Information */}
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold mb-4 flex items-center">
                      <Mail className="w-5 h-5 mr-2" />
                      Contact Information
                    </h2>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={() => {
                          if (email && !validateEmail(email)) {
                            setEmailError('Please enter a valid email address');
                          } else {
                            setEmailError('');
                          }
                        }}
                        placeholder="you@example.com"
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent ${
                          emailError ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {emailError && (
                        <p className="mt-1 text-sm text-red-500">{emailError}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Shipping Address */}
              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold mb-4 flex items-center">
                      <MapPin className="w-5 h-5 mr-2" />
                      Shipping Address
                    </h2>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={shippingAddress.name}
                          onChange={(e) => setShippingAddress({...shippingAddress, name: e.target.value})}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-purple ${
                            addressErrors.name ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {addressErrors.name && <p className="mt-1 text-sm text-red-500">{addressErrors.name}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Address Line 1 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={shippingAddress.address1}
                          onChange={(e) => setShippingAddress({...shippingAddress, address1: e.target.value})}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-purple ${
                            addressErrors.address1 ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {addressErrors.address1 && <p className="mt-1 text-sm text-red-500">{addressErrors.address1}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Address Line 2 (optional)
                        </label>
                        <input
                          type="text"
                          value={shippingAddress.address2}
                          onChange={(e) => setShippingAddress({...shippingAddress, address2: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            City <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={shippingAddress.city}
                            onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-purple ${
                              addressErrors.city ? 'border-red-500' : 'border-gray-300'
                            }`}
                          />
                          {addressErrors.city && <p className="mt-1 text-sm text-red-500">{addressErrors.city}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            State <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={shippingAddress.state}
                            onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                            placeholder="NY"
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-purple ${
                              addressErrors.state ? 'border-red-500' : 'border-gray-300'
                            }`}
                          />
                          {addressErrors.state && <p className="mt-1 text-sm text-red-500">{addressErrors.state}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            ZIP Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={shippingAddress.zip}
                            onChange={(e) => setShippingAddress({...shippingAddress, zip: e.target.value})}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-purple ${
                              addressErrors.zip ? 'border-red-500' : 'border-gray-300'
                            }`}
                          />
                          {addressErrors.zip && <p className="mt-1 text-sm text-red-500">{addressErrors.zip}</p>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Country <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={shippingAddress.country}
                            onChange={(e) => setShippingAddress({...shippingAddress, country: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple"
                          >
                            <option value="US">United States</option>
                            <option value="CA">Canada</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone (optional)
                        </label>
                        <input
                          type="tel"
                          value={shippingAddress.phone}
                          onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Shipping Method */}
              {step === 3 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold mb-4 flex items-center">
                      <Truck className="w-5 h-5 mr-2" />
                      Shipping Method
                    </h2>

                    {loadingShipping ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader className="w-8 h-8 text-brand-purple animate-spin" />
                        <span className="ml-3 text-gray-600">Loading shipping options...</span>
                      </div>
                    ) : shippingRates.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-600">No shipping options available.</p>
                        <button
                          onClick={() => setStep(2)}
                          className="mt-4 text-brand-purple hover:underline"
                        >
                          Go back and check your address
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {shippingRates.map((rate, index) => (
                          <label
                            key={index}
                            className={`block p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                              selectedShipping?.carrier === rate.carrier && 
                              selectedShipping?.service_level === rate.service_level
                                ? 'border-brand-purple bg-purple-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center">
                              <input
                                type="radio"
                                name="shipping"
                                checked={
                                  selectedShipping?.carrier === rate.carrier && 
                                  selectedShipping?.service_level === rate.service_level
                                }
                                onChange={() => setSelectedShipping(rate)}
                                className="w-4 h-4 text-brand-purple focus:ring-brand-purple"
                              />
                              <div className="ml-4 flex-1">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {rate.carrier} - {rate.service_level}
                                    </p>
                                    {rate.estimated_days && (
                                      <p className="text-sm text-gray-600">
                                        Estimated delivery: {rate.estimated_days} {rate.estimated_days === 1 ? 'day' : 'days'}
                                      </p>
                                    )}
                                  </div>
                                  <p className="font-semibold text-gray-900">${rate.amount.toFixed(2)}</p>
                                </div>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 4: Payment */}
              {step === 4 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold mb-4 flex items-center">
                      <CreditCard className="w-5 h-5 mr-2" />
                      Payment Information
                    </h2>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Card Details <span className="text-red-500">*</span>
                        </label>
                        <div className="p-4 border border-gray-300 rounded-lg">
                          <CardElement
                            options={CARD_ELEMENT_OPTIONS}
                            onChange={(e) => {
                              setCardComplete(e.complete);
                              setCardError(e.error ? e.error.message : '');
                            }}
                          />
                        </div>
                        {cardError && (
                          <p className="mt-2 text-sm text-red-500">{cardError}</p>
                        )}
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600">
                          Your payment information is encrypted and secure. We never store your card details.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="mt-8 flex justify-between">
                {step > 1 && (
                  <button
                    onClick={() => setStep(step - 1)}
                    disabled={loading}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Back
                  </button>
                )}
                
                {step < 4 ? (
                  <button
                    onClick={handleNext}
                    disabled={loading || loadingShipping || (step === 3 && !selectedShipping)}
                    className="ml-auto px-6 py-3 bg-brand-purple text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading ? (
                      <>
                        <Loader className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Continue'
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handlePayment}
                    disabled={loading || !cardComplete || !stripe}
                    className="ml-auto px-8 py-3 bg-brand-purple text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-lg font-semibold"
                  >
                    {loading ? (
                      <>
                        <Loader className="w-5 h-5 mr-2 animate-spin" />
                        Processing Payment...
                      </>
                    ) : (
                      <>
                        Pay ${total.toFixed(2)}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right side - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
              
              {/* Cart Items */}
              <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                {cart.map((item, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    {item.product?.image_urls?.[0] && (
                      <img
                        src={item.product.image_urls[0]}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-sm text-gray-600">
                        Size: {item.size} | Qty: {item.quantity}
                      </p>
                    </div>
                    <p className="font-semibold">
                      ${((item.price || item.product?.price || 0) * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span>
                    {selectedShipping ? `$${shippingCost.toFixed(2)}` : 'Calculated at next step'}
                  </span>
                </div>
                {tax > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Tax</span>
                    <span>${tax.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              {/* Shipping Details Preview */}
              {step >= 2 && shippingAddress.name && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900 mb-2">Shipping to:</p>
                  <p className="text-sm text-gray-600">{shippingAddress.name}</p>
                  <p className="text-sm text-gray-600">
                    {shippingAddress.address1}
                    {shippingAddress.address2 && `, ${shippingAddress.address2}`}
                  </p>
                  <p className="text-sm text-gray-600">
                    {shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip}
                  </p>
                </div>
              )}

              {/* Selected Shipping Preview */}
              {step >= 3 && selectedShipping && (
                <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-sm font-medium text-gray-900 mb-1">Shipping Method:</p>
                  <p className="text-sm text-gray-700">
                    {selectedShipping.carrier} - {selectedShipping.service_level}
                  </p>
                  {selectedShipping.estimated_days && (
                    <p className="text-xs text-gray-600 mt-1">
                      Est. delivery: {selectedShipping.estimated_days} {selectedShipping.estimated_days === 1 ? 'day' : 'days'}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CheckoutSinglePage = ({ cart, cartTotal, getCartItemCount, clearCart }) => {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm cart={cart} cartTotal={cartTotal} clearCart={clearCart} />
    </Elements>
  );
};

export default CheckoutSinglePage;
